/**
 * Class Card Component
 * Display class information in a card format with actions
 */

import { Card, CardContent, CardFooter, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import type { Class } from '@cloak-next/shared';
import { formatDistanceToNow } from 'date-fns';

interface ClassCardProps {
  classData: Class;
  onViewDetails: (className: string) => void;
  onProvisionLabs?: (className: string) => void;
  onTerminateLabs?: (className: string) => void;
}

/**
 * Get badge variant based on class status
 */
function getStatusBadgeVariant(status: Class['status']) {
  switch (status) {
    case 'draft':
      return 'secondary';
    case 'scheduled':
      return 'default';
    case 'active':
      return 'default'; // Will be styled with green
    case 'ended':
      return 'outline';
    default:
      return 'secondary';
  }
}

/**
 * Get capacity indicator color
 */
function getCapacityColor(current: number, max: number): string {
  const percentage = (current / max) * 100;
  if (percentage >= 95) return 'text-red-600';
  if (percentage >= 75) return 'text-yellow-600';
  return 'text-green-600';
}

/**
 * Get capacity indicator emoji
 */
function getCapacityIndicator(current: number, max: number): string {
  const percentage = (current / max) * 100;
  if (percentage >= 95) return '🔴';
  if (percentage >= 75) return '🟡';
  return '🟢';
}

/**
 * Format date range display
 */
function formatDateRange(startTime: string, endTime: string): string {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const now = new Date();

  if (now < start) {
    // Future class
    return `Starts ${formatDistanceToNow(start, { addSuffix: true })}`;
  } else if (now > end) {
    // Past class
    return `Ended ${formatDistanceToNow(end, { addSuffix: true })}`;
  } else {
    // Active class
    return `Ends ${formatDistanceToNow(end, { addSuffix: true })}`;
  }
}

export function ClassCard({
  classData,
  onViewDetails,
  onProvisionLabs,
  onTerminateLabs,
}: ClassCardProps) {
  const totalCapacity = classData.maxStudents;
  const capacityColor = getCapacityColor(classData.currentStudents, totalCapacity);
  const capacityIndicator = getCapacityIndicator(classData.currentStudents, totalCapacity);

  const canProvision = classData.status !== 'draft' && classData.status !== 'ended' && !classData.labsProvisioned;
  const canTerminate = classData.labsProvisioned;

  const copyClassCode = () => {
    navigator.clipboard.writeText(classData.classCode);
    // TODO: Add toast notification
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">{classData.className}</h3>
            <div className="flex items-center gap-2">
              <Badge variant={getStatusBadgeVariant(classData.status)}>
                {classData.status.toUpperCase()}
              </Badge>
              <span className="text-sm text-muted-foreground">
                Code:{' '}
                <button
                  onClick={copyClassCode}
                  className="font-mono hover:underline"
                  title="Click to copy"
                >
                  {classData.classCode}
                </button>
              </span>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-2">
        <div className="text-sm text-muted-foreground">
          {formatDateRange(classData.startTime, classData.endTime)}
        </div>

        <div className="flex items-center gap-4 text-sm">
          <span className={capacityColor}>
            {capacityIndicator} {classData.currentStudents}/{totalCapacity} students
          </span>
        </div>

        <div className="text-sm">
          <span className="text-muted-foreground">Labs: </span>
          {classData.labsProvisioned ? (
            <span className="text-green-600">✅ Ready</span>
          ) : (
            <span className="text-yellow-600">⚠️ Not Provisioned</span>
          )}
        </div>
      </CardContent>

      <CardFooter className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewDetails(classData.className)}
        >
          View Details
        </Button>

        {canProvision && onProvisionLabs && (
          <Button
            variant="default"
            size="sm"
            onClick={() => onProvisionLabs(classData.className)}
          >
            Provision Labs
          </Button>
        )}

        {canTerminate && onTerminateLabs && (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onTerminateLabs(classData.className)}
          >
            Terminate Labs
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
